import { assert } from "@animech/core-utilities";
import {
	AttacherInstance,
	AttacherNode,
	AttacherNodesByModelId,
	AttacherProperties,
} from "@animech/supermodel";
import { objectKeys } from "src/utility";

type Filter<T> = (value: T) => boolean;

type ValueOrFilter<T> = T | Filter<T>;

type ValueOrFilterObject<T> = {
	[P in keyof T]: ValueOrFilter<T[P]>;
};

type FilterValue<T> = T extends Filter<infer U> ? U : never;

function isFilter<T>(value: T): value is T & Filter<FilterValue<T>> {
	return typeof value === "function";
}

export type AttacherIdentifier = Partial<
	ValueOrFilterObject<Omit<AttacherProperties, "errors" | "tags">>
>;

export type AttributeValueIdentifier = {
	attachers?: AttacherIdentifier[];
	modelIds?: string[];
};

export type BaseAttributeValuesDefinition = Record<
	string | number,
	AttributeValueIdentifier[]
>;

export type BaseAttributeDefinition = {
	defaultValue: string | number;
	values: BaseAttributeValuesDefinition;
};

export type BaseAttributeDefinitions = Record<string, BaseAttributeDefinition>;

/**
 * A map of "attributes" by attacher, which allows them to first be marked with
 * arbitrary string literals, and then later identified thereby. This can come
 * in handy when implementing complex attacher rules, which can then be broken
 * down into attribute-specific parts.
 */
export class AttacherAttributeMap<
	AttributeDefinitions extends BaseAttributeDefinitions,
	AttributeType extends keyof AttributeDefinitions,
> {
	private readonly attributeNodeMaps: Record<
		AttributeType,
		WeakMap<AttacherNode, keyof AttributeDefinitions[AttributeType]["values"]>
	>;
	private readonly attributeDefaults: Record<
		AttributeType,
		AttributeDefinitions[AttributeType]["defaultValue"]
	>;

	constructor(
		attacherNodesByModelId: AttacherNodesByModelId,
		dynamicAttacherNodesByEntityName: Record<string, AttacherNode[]>,
		definitions: AttributeDefinitions,
	) {
		const attributeTypes = objectKeys(definitions).filter(
			(key): key is AttributeType => typeof key === "string",
		);
		this.attributeNodeMaps = {} as typeof this.attributeNodeMaps;
		this.attributeDefaults = {} as typeof this.attributeDefaults;

		for (const attributeType of attributeTypes) {
			this.attributeNodeMaps[attributeType] = this.createAttributeNodeMap(
				attacherNodesByModelId,
				dynamicAttacherNodesByEntityName,
				definitions,
				attributeType,
			);
			this.attributeDefaults[attributeType] =
				definitions[attributeType].defaultValue;
		}
	}

	public get<NarrowedAttributeType extends AttributeType>(
		attributeType: NarrowedAttributeType,
		attacherInstance: AttacherInstance,
	):
		| keyof AttributeDefinitions[NarrowedAttributeType]["values"]
		| AttributeDefinitions[NarrowedAttributeType]["defaultValue"] {
		const attribute = this.attributeNodeMaps[attributeType];

		return (
			attribute.get(attacherInstance.node) ??
			this.attributeDefaults[attributeType] ??
			undefined
		);
	}

	private createAttributeNodeMap<NarrowedAttributeType extends AttributeType>(
		attacherNodesByModelId: AttacherNodesByModelId,
		dynamicAttacherNodesByEntityName: Record<string, AttacherNode[]>,
		definitions: AttributeDefinitions,
		attributeType: NarrowedAttributeType,
	): WeakMap<
		AttacherNode,
		keyof AttributeDefinitions[NarrowedAttributeType]["values"]
	> {
		const result = new WeakMap();

		const processMatchingAttributeValues = (
			attacherNode: AttacherNode,
			matchingAttributeValues: (keyof AttributeDefinitions[AttributeType]["values"])[],
		) => {
			if (matchingAttributeValues.length <= 0) {
				return;
			}

			assert(
				matchingAttributeValues.length === 1,
				"Logical overlap found in attribute definitions",
			);

			result.set(attacherNode, matchingAttributeValues[0]);
		};

		for (const [modelId, attacherNodes] of Object.entries(
			attacherNodesByModelId,
		)) {
			for (const attacherNode of attacherNodes) {
				const matchingAttributeValues = this.findMatchingAttributeValues(
					definitions,
					attributeType,
					attacherNode,
					modelId,
				);

				processMatchingAttributeValues(attacherNode, matchingAttributeValues);
			}
		}

		for (const [_entityName, attacherNodes] of Object.entries(
			dynamicAttacherNodesByEntityName,
		)) {
			for (const attacherNode of attacherNodes) {
				const matchingAttributeValues = this.findMatchingAttributeValues(
					definitions,
					attributeType,
					attacherNode,
					undefined,
				);

				processMatchingAttributeValues(attacherNode, matchingAttributeValues);
			}
		}

		return result;
	}

	private findMatchingAttributeValues<
		NarrowedAttributeType extends AttributeType,
		ValueType extends
			keyof AttributeDefinitions[NarrowedAttributeType]["values"],
	>(
		attributeDefinitions: AttributeDefinitions,
		attributeType: NarrowedAttributeType,
		attacherNode: AttacherNode,
		modelId?: string,
	): ValueType[] {
		const result: ValueType[] = [];
		const attributeDefinition = attributeDefinitions[attributeType];
		const valueTypes = objectKeys(attributeDefinition.values);

		for (const valueType of valueTypes) {
			const valueIdentifiers = attributeDefinition.values[valueType];

			for (const valueIdentifier of valueIdentifiers) {
				if (!AttacherAttributeMap.modelIdsMatch(valueIdentifier, modelId)) {
					continue;
				}

				if (
					!AttacherAttributeMap.attachersMatch(valueIdentifier, attacherNode)
				) {
					continue;
				}

				result.push(valueType as ValueType);
			}
		}

		return result;
	}

	private static modelIdsMatch(
		attributeValueIdentifier: AttributeValueIdentifier,
		modelId?: string,
	): boolean {
		return (
			!attributeValueIdentifier.modelIds ||
			(!!modelId && attributeValueIdentifier.modelIds.includes(modelId))
		);
	}

	private static attachersMatch(
		attributeValueIdentifier: AttributeValueIdentifier,
		attacherNode: AttacherNode,
	): boolean {
		return (
			!attributeValueIdentifier.attachers ||
			attributeValueIdentifier.attachers.some((attacherIdentifier) => {
				const propertyKeys = objectKeys(attacherIdentifier);

				for (const propertyKey of propertyKeys) {
					const definitionProperty = attacherIdentifier[propertyKey];
					const nodeProperty = attacherNode[propertyKey];

					if (isFilter(definitionProperty)) {
						return definitionProperty(nodeProperty);
					} else if (nodeProperty !== definitionProperty) {
						return false;
					}
				}

				return true;
			})
		);
	}
}
